import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import {
  GET_CONTROLLER_RESTART_STATUS_API,
  getControllerRestartStatus,
} from '@/features/controller/endpoints/controllers'
import { useControllerRestartState } from '@/features/controller/restart/hooks/useControllerRestartState'
import { ExecutionStatus } from '@/features/controller/restart/types'

interface UseControllerRestartStatusPollingProps {
  controllerId: string
  enabled: boolean
  onSuccess?: () => void
  onError?: () => void
}

export function useControllerRestartStatusPolling({
  controllerId,
  enabled,
  onSuccess,
  onError,
}: UseControllerRestartStatusPollingProps) {
  const {
    restartStatus: previousRestartStatus,
    setRestartInProgress,
    setRestartSuccess,
    setRestartError,
  } = useControllerRestartState(controllerId)

  const previousRenderRestartStatusRef = useRef<typeof previousRestartStatus>(null)

  // Poll if enabled - always allow polling for page loads (previousRestartStatus === null)
  // For active restarts (previousRestartStatus === 'restarting'), polling continues normally
  const shouldPoll = enabled && (previousRestartStatus === null || previousRestartStatus === 'restarting')

  const { data, error, refetch } = useQuery({
    queryKey: [GET_CONTROLLER_RESTART_STATUS_API, controllerId],
    queryFn: () => getControllerRestartStatus(controllerId),
    enabled: shouldPoll,
    refetchInterval: previousRestartStatus === 'restarting' ? 2000 : false,
    refetchIntervalInBackground: false,
    retry: false,
    gcTime: Infinity, // Keep data in cache indefinitely
    staleTime: 0, // Always consider data stale so it refetches when needed
  })

  // Trigger immediate refetch when restart status changes to 'restarting'
  useEffect(() => {
    if (previousRestartStatus === 'restarting' && previousRenderRestartStatusRef.current !== 'restarting') {
      refetch()
    }
    previousRenderRestartStatusRef.current = previousRestartStatus
  }, [previousRestartStatus, refetch])

  useEffect(() => {
    if (!data) return

    const lastRestart = data.lastRestart

    // Handle cases where we have restart information
    if (lastRestart) {
      switch (lastRestart.status) {
        case ExecutionStatus.SUCCEEDED:
          if (previousRestartStatus !== 'success') {
            setRestartSuccess()
            onSuccess?.()
          }
          return
        case ExecutionStatus.FAILED:
        case ExecutionStatus.TIMED_OUT:
        case ExecutionStatus.CANCELED:
        case ExecutionStatus.REJECTED:
        case ExecutionStatus.REMOVED:
          if (previousRestartStatus !== 'error') {
            setRestartError()
            onError?.()
          }
          return
        case ExecutionStatus.QUEUED:
        case ExecutionStatus.IN_PROGRESS:
          // If we weren't already tracking this restart, start tracking it
          // Also re-track if previous restart finished (error/success) and a new one started
          if (
            previousRestartStatus === null ||
            previousRestartStatus === 'error' ||
            previousRestartStatus === 'success'
          ) {
            setRestartInProgress()
          }
          return
      }
    }

    // If there's no active restart, and we were previously restarting, mark as error
    // This handles edge cases where the restart completed but API didn't return lastRestart data
    if (!data.hasActiveRestart && previousRestartStatus === 'restarting') {
      setRestartError()
      onError?.()
    }
  }, [data, setRestartSuccess, setRestartError, setRestartInProgress, previousRestartStatus, onSuccess, onError])

  useEffect(() => {
    if (error && shouldPoll) {
      setRestartError()
      onError?.()
    }
  }, [error, shouldPoll, setRestartError, onError])

  return {
    restartResponse: data,
    lastRestart: data?.lastRestart,
    isPolling: shouldPoll && previousRestartStatus === 'restarting', // Actively polling when restarting
    isLoading: shouldPoll && !data && !error, // Loading while fetching initial data
    error,
  }
}
